import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Send, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Markdown from "@/components/Markdown";

export default function Chatbot() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [bot, setBot] = useState({ name: "DrkCECO", avatar: null });
  const bodyRef = useRef(null);

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{ role: "assistant", text: `Hi ${user?.name?.split(" ")[0] || "there"}! I'm DrkCECO. Ask me anything about hosting, Minecraft, billing, or your account.` }]);
    }
  }, [user]);

  useEffect(() => { bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight }); }, [messages, thinking]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput(""); setThinking(true);
    try {
      const { data } = await api.post("/chatbot/message", { session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setBot({ name: data.bot_name || "DrkCECO", avatar: data.bot_avatar });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, articles: data.used_articles }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Something went wrong. Try again in a moment." }]);
    } finally { setThinking(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <header>
        <div className="font-mono text-[10px] tracking-widest uppercase text-primary mb-2">// AI Assistant</div>
        <h1 className="font-display font-black text-4xl tracking-tighter flex items-center gap-3">
          {bot.avatar && <img src={bot.avatar} alt={bot.name} className="h-10 w-10 rounded-lg object-cover" />}
          {bot.name}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Powered by Claude Sonnet 4.5 · trained on your knowledge base.</p>
      </header>

      <div ref={bodyRef} className="rounded-lg border border-border/60 bg-card h-[520px] overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => {
          const isUser = m.role === "user";
          return (
            <div key={i} className={`flex gap-2 ${isUser ? "flex-row-reverse" : ""}`}>
              <div className="h-8 w-8 rounded-full overflow-hidden bg-secondary flex items-center justify-center text-xs font-semibold shrink-0">
                {isUser ? (user?.name || "?").slice(0,1).toUpperCase() : (bot.avatar ? <img src={bot.avatar} className="w-full h-full object-cover" /> : "D")}
              </div>
              <div className={`px-3 py-2 rounded-xl max-w-[80%] text-sm leading-relaxed ${isUser ? "bg-primary text-primary-foreground rounded-br-sm whitespace-pre-wrap" : "bg-secondary/60 border border-border/60 rounded-bl-sm"}`}>
                {isUser ? m.text : <Markdown>{m.text}</Markdown>}
                {!isUser && m.articles && m.articles.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/40 text-xs text-muted-foreground">
                    <div className="font-mono text-[9px] uppercase tracking-widest">Sources</div>
                    {m.articles.map((a) => <div key={a.article_id}>• {a.title}</div>)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {thinking && <div className="text-xs text-muted-foreground pl-10"><Sparkles className="h-3 w-3 inline mr-1 animate-pulse" /> {bot.name} is thinking…</div>}
      </div>

      <div className="flex items-end gap-2">
        <textarea
          data-testid="chatbot-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={2}
          placeholder="Ask anything…"
          className="flex-1 bg-card border border-border/60 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:border-primary/50"
        />
        <Button data-testid="chatbot-send" onClick={send} disabled={thinking || !input.trim()} className="gap-2 h-11"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
